import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, UntypedFormControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ESkeletonType } from '@norfolk-southern/accessns-components';
import { saveAs } from 'file-saver';

import { IRailEmissionCompany, IRailEmissionReport, IRailEmissionRequest } from '@ruby/modules/rail-emission/models/rail-emission.interface';

@Component({
  selector: 'ng-rail-emission',
  templateUrl: './rail-emission.component.html',
  styleUrls: ['./rail-emission.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RailEmissionComponent implements OnChanges {
  @Input() loadingCompanies!: boolean;
  @Input() loading!: boolean;
  @Input() companies!: Array<IRailEmissionCompany>;
  @Input() reports!: Array<IRailEmissionReport>;
  @Output() report = new EventEmitter<IRailEmissionRequest>();
  @Output() resetReport = new EventEmitter();
  @Output() auditDownload = new EventEmitter<IRailEmissionRequest>();
  years: Array<string> = [];
  selectedIndustries: Array<IRailEmissionReport> = [];
  maskType: ESkeletonType = ESkeletonType.WIDGET;
  displayPreView = false;
  displayYear = true;
  company = new UntypedFormControl(undefined, [
    Validators.required,
    this.companyValidator().bind(this)
  ]);
  year = new UntypedFormControl({ value: undefined, disabled: true }, Validators.required);
  selectedCompany?: IRailEmissionCompany;
  selectedReport?: IRailEmissionReport;
  tooltips: Record<string, string> = {
    Intermodal: 'Emissions summary for NS shipments of intermodal containers',
    'Finished Vehicles': 'Emissions summary for NS shipments of finished automobiles',
    'Industrial Products': 'Emissions summary for all NS shipments, other than intermodal and finished vehicles'
  };

  constructor() { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.companies?.currentValue?.length === 1) {
      this.company.setValue(changes.companies.currentValue[0]);
    }
    if (changes.reports) {
      if (changes.reports.currentValue?.length > 0) {
        this.displayPreView = true;
        this.selectedReport = changes.reports.currentValue[0];
      } else {
        this.displayPreView = false;
        this.selectedReport = undefined;
      }
    }
  }

  /**
   * Get Custom Text
   *
   * @summary validates company/year and returns custom text
   * @returns string
   */
  getCustomText(): string {
    let customText = '';
    if (this.company.value?.ultimateCompany) {
      if (this.company.value.years.length > 0) {
        if (this.year.value) {
          if (this.reports.length === 0 && !this.loading) {
            customText = 'No reports available';
          }
        } else {
          customText = 'Select year to view available reports';
        }

      } else {
        customText = `Sorry, there are no reports available for ${ this.company.value.ultimateCompany }`;
      }

    } else {
      customText = 'Select company and year to view available reports';
    }
    return customText;
  }

  /**
   * Company Validator
   *
   * @summary validates selected company and enables year drop down
   * @returns ValidationFn
   */
  companyValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      if (control.value?.ultimateCompany !== this.selectedCompany?.ultimateCompany) {
        this.displayYear = true;
        this.selectedCompany = control.value;
        this.resetReport.emit();
        this.year.reset();
        this.year.disable();
        if (control.value) {
          if (control.value.years.length > 0) {
            this.year.enable();
            this.years = control.value.years;
            if (this.years.length === 1) {
              this.year.setValue(this.years[0]);
              this.yearSelected(this.years[0]);
            }
          } else {
            this.displayYear = false;
          }
        }
      }
      return {};
    };
  }

  /**
   * Year Selected
   *
   * @summary Loads available reports for selected company and date
   * @param $value?: string
   * @returns void
   */
  yearSelected($value?: string): void {
    if ($value) {
      this.report.emit({ year: this.year.value, ultimateCompany: this.company.value.ultimateCompany });
      this.displayPreView = false;
    }
  }

  /**
   * Download
   *
   * @summary Downloads the selected reports
   * @returns void
   */

  /* istanbul ignore next */
  download(): void {
    this.selectedIndustries.forEach((value: IRailEmissionReport) => {
      const buf: Uint8Array = Uint8Array.from(window.atob(value.base64), (v: string) => v.charCodeAt(0));
      saveAs(new Blob([buf]), value.fileName);
    });
    this.auditDownload.emit({ year: this.year.value, ultimateCompany: this.company.value.ultimateCompany });
  }

  /**
   * Checked
   *
   * @summary Updates selected industries
   * @param $check: boolean
   * @param $report: IRailEmissionReport
   * @returns void
   */
  checked($check: boolean, $report: IRailEmissionReport): void {
    const match: IRailEmissionReport | undefined = this.selectedIndustries.find((item: IRailEmissionReport) => item.fileName === $report.fileName);
    if ($check && !match) {
      this.selectedIndustries.push($report);
    } else if (!$check && match) {
      this.selectedIndustries = this.selectedIndustries.filter((item: IRailEmissionReport) => item.fileName !== $report.fileName);
    }
  }

  /**
   * Selected Pdf
   *
   * @summary Updates selected value for the report
   * @param $report: IRailEmissionReport
   * @returns void
   */
  selectedPdf($report: IRailEmissionReport): void {
    this.selectedReport = $report;
    this.reports = this.reports.map((item: IRailEmissionReport) => ({
      ...item,
      selected: $report.reportName === item.reportName
    }));
  }
}
